import React, { useEffect } from 'react';
import produce from 'immer';
import type { EditorState } from 'lexical';
import { twMerge } from 'tailwind-merge';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { LexicalInput } from '@components/Lexical/Input';
import { useLexicalMentionsMenuContainerMarginClassName } from '@components/Lexical/useLexicalMentionsMenuBottomPadding';
import { LexicalUtils } from '@components/Lexical/utils';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { commentReplyFilterQueryKey } from '@components/main-components/user/shared';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  AddPostCommentReplyDocument,
  CommentRepliesDocument,
  PostModalCommentDocument,
  setInfiniteQueryData,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { Comment, ParentComment } from './shared';

export function ReplyCommentWithParentCommentModal({
  postCommentId,
  parentPostCommentId,
}: {
  postCommentId: string;
  parentPostCommentId: string;
}) {
  const { openToast } = useToast();
  const { closeModal } = useModal();

  const logError = useLogError();

  const { mutateAsync: postReply, isLoading: postingReply } = useMutation(
    AddPostCommentReplyDocument,
    {
      retry: 3,
    },
  );

  const { data: parentCommentData, isLoading: isLoadingParentComment } = useQuery(
    PostModalCommentDocument,
    {
      variables: {
        postCommentId: parentPostCommentId,
      },
      staleTime: 0,
    },
  );

  const { data: commentData, isLoading: isLoadingComment } = useQuery(PostModalCommentDocument, {
    variables: {
      postCommentId,
    },
    staleTime: 0,
  });

  const comment = commentData?.data.postComment;
  const parentComment = parentCommentData?.data.postComment;

  const [hasOverflow, setHasOverflow] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const submitReply = useStableCallback(async (lexicalState: EditorState) => {
    const contentText = LexicalUtils.generateTextForState(lexicalState);
    if (!contentText.length) return;
    try {
      const {
        data: { addPostCommentReply },
      } = await postReply({
        input: {
          postCommentId: parentPostCommentId,
          lexicalContent: lexicalState.toJSON(),
        },
      });

      if (addPostCommentReply.__typename === 'MutationAddPostCommentReplySuccess') {
        openToast({
          text: 'Your reply was added',
          variant: 'success',
        });

        setInfiniteQueryData(
          {
            query: CommentRepliesDocument,
            filterQueryKey: commentReplyFilterQueryKey(postCommentId),
            options: {},
          },
          previousData => {
            const page = {
              data: {
                commentReplies: {
                  edges: [{ node: addPostCommentReply.data }],
                  pageInfo: {
                    endCursor: null,
                    hasNextPage: true,
                  },
                },
              },
            } satisfies NonNullable<typeof previousData>['pages'][number];

            if (!previousData) {
              return {
                pageParams: [],
                pages: [page],
              };
            }

            return produce(previousData, draft => {
              draft.pages.unshift(page);
            });
          },
        );

        closeModal();
      } else {
        logError({
          action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
          error: new Error(addPostCommentReply.message),
          // Swap for REPLY_ERROR
          errorType: ERROR_TYPE.UNKNOWN,
          isArtistOnlyPage: false,
          level: 'error',
          message: addPostCommentReply.message,
          pillar: 'POSTING',
          toast: addPostCommentReply.message,
          indexedTags: {
            postCommentId,
            content: contentText,
          },
        });
        return;
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.REPLY_POST_COMMENT_FAILED,
        error,
        // Swap for REPLY_ERROR
        errorType: ERROR_TYPE.UNKNOWN,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'There was an error replying to a comment',
        pillar: 'POSTING',
        toast: 'There was an error posting your reply. Please try again.',
        indexedTags: {
          postCommentId,
          content: contentText,
        },
      });
    }
  });

  useEffect(() => {
    if (!bodyRef.current) {
      return;
    }
    setHasOverflow(bodyRef.current.clientHeight < bodyRef.current.scrollHeight);
  }, [bodyRef]);

  const extraMarginClassName = useLexicalMentionsMenuContainerMarginClassName();

  return (
    <StandardModal
      ref={bodyRef}
      mobileFullScreen
      title="Reply"
      body={
        <div className={twMerge('flex h-full flex-col gap-5', extraMarginClassName)}>
          {/* Parent Comment */}
          <ParentComment
            author={parentComment?.author}
            content={parentComment?.content}
            createdAt={parentComment?.createdAt}
            isLoading={isLoadingParentComment}
            lexicalContent={parentComment?.lexicalContent}
            variant="default"
          />

          {/* Comment */}
          <Comment
            author={comment?.author}
            content={comment?.content}
            createdAt={comment?.createdAt}
            isLoading={isLoadingComment}
            lexicalContent={comment?.lexicalContent}
          />

          {!comment ? (
            <div className="h-10 w-full animate-pulse rounded-sm bg-neutral200" />
          ) : (
            <LexicalInput
              initialMentionData={{
                displayName:
                  comment.author.artist?.name ||
                  comment.author.displayName ||
                  comment.author.publicAddress,
                artistId: comment.author.artist?.id || null,
                userId: comment.author.id,
                avatarUrl: comment.author.avatar?.url || null,
                webappUri: comment.author.webappUri,
                key: `mention-${comment.author.id}`,
              }}
              menuAnchorClassName="z-modalDropdown"
              className="mb-1"
              loading={postingReply}
              onSubmit={submitReply}
              characterCount={false}
              disabled={false}
              id={undefined}
              type="single-line"
              placeholder={`Reply to ${comment?.author?.username}`}
              onChange={null}
              autoFocus
            />
          )}
        </div>
      }
      closeModal={closeModal}
      bodyClassName="md:overflow-auto"
      showHeaderShadow={hasOverflow}
      preventClose={postingReply}
      bodyWithoutPadding
    />
  );
}
